import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import classNames from 'clsx';
import { groupBy } from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { Popper } from 'react-popper';
import { useSelector } from 'react-redux';

import { useWidget } from '@swf/core/hooks/useWidget';
import { select } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import { getTablePropsFromGame } from '@swf/core/utils/casino';
import getBackgroundImageStyle from '@swf/core/utils/getBackgroundImageStyle';

import { getGameDeveloperFromTables } from '../../utils/common';
import BonusFeatures from '../BonusFeatures/BonusFeatures';
import CloseButton from '../Buttons/CloseButton';
import InfoPlayButton from '../Buttons/InfoPlayButton';
import WatchTrailerButton from '../Buttons/WatchTrailerButton';
import CasinoGameIcon from '../CasinoGameIcon/CasinoGameIcon';
import CasinoGameIconSL from '../CasinoGameIconSL/CasinoGameIconSL';
import type { SLProps } from '../CasinoGameList/CasinoGameList';
import ProviderLogo from '../CasinoProviderLogo/CasinoProviderLogo';
import type { PROVIDER_LOGO_MAPPING } from '../CasinoProviderLogo/constants';
import GameTrailerIcon from '../GameTrailerIcon/GameTrailerIcon';

import styles from './CasinoGameInfoCardMax.scss';
import BetInfo from './components/BetInfo';

export interface Props {
  isModal?: boolean;
  fsbLaunch?: boolean;
  raceId?: string;
  game: CasinoGame;
}

const CasinoGameInfoCardMax: React.FC<Props> = (props) => {
  const { widgetConfig } = useWidget();
  const { fsbLaunch, game, isModal, raceId } = props;
  const [detailVisible, setDetailVisible] = useState<boolean>(false);
  const [videoVisible, setVideoVisible] = useState<boolean>(false);
  const ref = useRef(null);
  const [betFeatureCount, setBetFeatureCount] = useState<number>(-1);
  const descriptionEnabled = useFeature(FeatureFlag.CasinoGameInfoDescription);
  const newTileViewEnabled = useFeature(FeatureFlag.CasinoGameInfoNewTileView);

  const siteId = useSelector(select.cms.getSiteId);
  const isMobile = useSelector(select.device.isMobile);
  const table = useMemo(() => getTablePropsFromGame(game), [game]);
  const tables = useMemo(() => groupBy(game.tables, 'playType'), [game.tables]);
  const developer = useMemo(() => getGameDeveloperFromTables(tables), [tables]);

  const trailerAvailable = game?.images.icnGameTrailerVideo;
  const isNewSLView = (widgetConfig as SLProps)?.new_game_tile_view ?? newTileViewEnabled;

  return (
    <div
      className={classNames(styles.container, isModal && styles.modal)}
      onClick={(evt) => evt.stopPropagation()}
    >
      <div className={classNames(styles.gamePage)}>
        <div className={styles.title}>{game.name}</div>
        {isModal && (
          <CloseButton
            className={styles.closeButton}
            closeModal
            onClick={() => setVideoVisible(false)}
          />
        )}
        {(detailVisible || videoVisible) && (
          <div className={classNames(styles.popper, styles.popperOverlay)} onClick={() => false} />
        )}
        {(detailVisible || videoVisible) && (
          <Popper>
            {() =>
              detailVisible && table ? (
                <BonusFeatures
                  className={classNames(styles.popper, styles.detailPopper)}
                  featureNames={table.featureNames}
                  properties={table.properties}
                  onClose={() => setDetailVisible(false)}
                  isInfoCard
                />
              ) : (
                <div className={classNames(styles.popper, styles.videoPopper)}>
                  <CloseButton
                    className={styles.closeButton}
                    onClick={() => {
                      setVideoVisible(false);
                    }}
                  />
                  <GameTrailerIcon game={game} autoPlay />
                </div>
              )
            }
          </Popper>
        )}
        <div className={classNames(styles.imageContainer)}>
          {isNewSLView ? (
            <CasinoGameIconSL game={game} gameInfo visible disabled scale={false} isInfoCard />
          ) : (
            <CasinoGameIcon
              game={game}
              disabled
              scale={false}
              className={styles.gameIcon}
              icon={isMobile ? 'icnWide' : 'icnSquare'}
            />
          )}
        </div>
        <div className={classNames(styles.launchBlock, trailerAvailable && styles.withTrailer)}>
          <InfoPlayButton game={game} fsbLaunch={fsbLaunch} raceId={raceId} />

          {trailerAvailable && (
            <WatchTrailerButton
              onClick={() => {
                setVideoVisible(true);
              }}
            />
          )}
        </div>
        <div
          className={classNames(
            styles.detailsContainer,
            table?.featureNames && styles.features,
            betFeatureCount === 0 && styles.noBetInfo,
            developer && styles.withProvider
          )}
          ref={ref}
        >
          {table ? (
            <BonusFeatures
              featureNames={table.featureNames}
              properties={table.properties}
              onClick={() => setDetailVisible(true)}
              hideInfoIcon
              isInfoCard
            />
          ) : null}

          {betFeatureCount !== 0 && table && (
            <BetInfo
              setBetFeatureCount={setBetFeatureCount}
              data={table}
              siteId={siteId}
              shortText
            />
          )}
          {developer && (
            <div className={styles.providerSection}>
              <ProviderLogo
                developer={developer as keyof typeof PROVIDER_LOGO_MAPPING}
                contentType="casino_game_info"
                customClassName={styles.customProviderLogo}
              />
            </div>
          )}
          {descriptionEnabled && game.desc && (
            <div className={classNames(styles.description)}>
              <p
                className={styles.gameDescription}
                dangerouslySetInnerHTML={{ __html: game.desc }}
              />
            </div>
          )}
        </div>
      </div>
      {game.images && (
        <div
          className={styles.infoBackground}
          style={getBackgroundImageStyle(game.images.icnSquare)}
        />
      )}
    </div>
  );
};

export default CasinoGameInfoCardMax;
